import PointOfSale from '@mui/icons-material/PointOfSale'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import Table from '@mui/icons-material/TableBar'
import ListAltIcon from '@mui/icons-material/ListAlt'
import DeviceIcon from '@mui/icons-material/Devices'
import CreditScore from '@mui/icons-material/CreditScore'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import React, { useMemo } from 'react'
import { DASHBOARD_DASHBOARD_ADMIN } from 'constant/permissions'
import ROUTE from 'constant/route'

import { useAppSelector } from 'store/useStoreHook'
import nameof from 'utilities/nameof'
import { OperationalSettingContract, ClientType } from 'models'
import { IRoute } from 'interfaces'
import usePermission from 'hooks/usePermission/usePermission'
import useAdminRoute from './useAdminRoute'
import { AuthRoute } from './AuthRoute'

const OPERATION_SETTINGS = nameof<OperationalSettingContract>()

export interface IGlobalMenu extends IRoute {
    name: string
    logo?: any
    popUp?: boolean
    paths?: string[]
    visibleToClientType?: ClientType[]
    disabledButton?: boolean
}

export function useOperationGlobalMenu() {
    const isOffline = useAppSelector((state) => state.ui.appState.isOffline)
    const operationSetting: any = useAppSelector(
        (state) => state.operation.data,
    )
    const currentClient = useAppSelector((state) => state.client.currentClient)

    const { hasPermission } = usePermission()
    const { adminRoutes } = useAdminRoute()

    const globalMenus: IGlobalMenu[] = useMemo(() => {
        const menus: IGlobalMenu[] = [
            {
                name: 'global_menus.admin',
                path: ROUTE.TOTAL_SALE_DASHBOARD,
                logo: <AdminPanelSettingsIcon />,
                paths: adminRoutes
                    .map((route) => route.path)
                    .filter((i) =>
                        hasPermission([DASHBOARD_DASHBOARD_ADMIN])
                            ? ![
                                  ROUTE.TERMINAL_PREFERENCE,
                                  ROUTE.QUICK_SERVICE,
                                  ROUTE.TABLE_SERVICE,
                                  ROUTE.ORDER_DASHBOARD,
                              ].some((r) => r === i)
                            : ![
                                  ROUTE.TERMINAL_PREFERENCE,
                                  ROUTE.QUICK_SERVICE,
                                  ROUTE.TABLE_SERVICE,
                              ].some((r) => r === i),
                    ),
                requirePermission: [],
                disabledButton: isOffline,
            },
            {
                ...AuthRoute.quickSale,
                name: 'global_menus.quick_sales',
                logo: <PointOfSale />,
            },
            {
                ...AuthRoute.tableSales,
                name: 'global_menus.table_sales',
                logo: <Table />,
                disabledButton: isOffline,
            },
            {
                ...AuthRoute.orderDashboard,
                name: 'global_menus.orders',
                logo: <ListAltIcon style={{ fill: '#ffff' }} />,
                disabledButton: isOffline,
            },
            {
                ...AuthRoute.posTerminal,
                name: 'global_menus.pos_terminal',
                logo: <ImportantDevicesIcon style={{ fill: '#ffff' }} />,
                disabledButton: isOffline,
            },
            {
                ...AuthRoute.cashDrawer,
                name: 'global_menus.cash_drawer',
                logo: <CreditScore style={{ fill: '#ffff' }} />,
                disabledButton: isOffline,
            },
            {
                name: 'global_menus.time_clock',
                path: ROUTE.TIME_CLOCK_POPUP,
                requirePermission: [],
                logo: <AccessTimeIcon />,
                popUp: true,
                moduleName: OPERATION_SETTINGS.timeClock,
                disabledButton: isOffline,
            },
            {
                name: 'global_menus.terminal_preferences',
                path: ROUTE.TERMINAL_PREFERENCE,
                requirePermission: [],
                logo: <DeviceIcon />,
                paths: [
                    ROUTE.TERMINAL_PREFERENCE,
                    ROUTE.TERMINAL_PREFERENCE_PRINTING,
                ],
                disabledButton: isOffline,
            },
        ]
        if (currentClient?.customerDisplaySettingEnable) {
            menus.push({
                name: 'global_menus.customer_facing_display',
                path: ROUTE.CUSTOMER_FACING_DISPLAY,
                moduleName: 'customerFacingDisplay',
                requirePermission: [],
                popUp: true,
                logo: <OpenInNewIcon />,
                paths: [ROUTE.CUSTOMER_FACING_DISPLAY],
            })
        }
        return menus
    }, [
        adminRoutes,
        isOffline,
        currentClient?.customerDisplaySettingEnable,
        hasPermission,
    ])
    const visitableGlobalMenus = useMemo(() => {
        return globalMenus
            .filter((g) => {
                let visible = true
                if (g.moduleName) {
                    visible = operationSetting[g.moduleName]
                }
                // only visible menus per client type
                if (visible && g.visibleToClientType) {
                    visible =
                        !!currentClient &&
                        g.visibleToClientType.includes(currentClient.type!)
                }
                // remove operational dashboard (orders) prevent duplicate
                if (
                    g.path === ROUTE.ORDER_DASHBOARD &&
                    !hasPermission([DASHBOARD_DASHBOARD_ADMIN])
                )
                    visible = false
                return visible
            })
            .map((i) => {
                if (i.path === ROUTE.TOTAL_SALE_DASHBOARD)
                    return {
                        ...i,
                        path: hasPermission([DASHBOARD_DASHBOARD_ADMIN])
                            ? ROUTE.TOTAL_SALE_DASHBOARD
                            : ROUTE.ORDER_DASHBOARD,
                    }
                return i
            })
    }, [globalMenus, operationSetting, currentClient, hasPermission])

    return visitableGlobalMenus
}
